import { useEffect, useRef, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import {
  getExpense,
  saveExpense,
  deleteExpense,
  uploadExpenseFile,
  deleteExpenseFile,
} from "../../api/expenses";
import { shrinkImage } from "../../utils/files";
import FileViewer from "./FileViewer";
import { categoryIcon } from "../../utils/expenseCategories";

// Add or edit one expense, or something still to buy. Receipts attach here.

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export default function ExpenseModal({ expenseId, initial, data, onClose }) {
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null); // a file record
  const fileRef = useRef(null);
  const changed = useRef(false);

  useEffect(() => {
    let off = false;
    if (expenseId) {
      getExpense(expenseId)
        .then((e) => {
          if (off) return;
          setForm({
            description: e.description || "",
            vendor: e.vendor || "",
            amount: e.amount_cents ? (e.amount_cents / 100).toFixed(2) : "",
            spent_on: e.spent_on ? String(e.spent_on).slice(0, 10) : "",
            category: e.category || "Other",
            account_id: e.account_id || "",
            status: e.status,
            notes: e.notes || "",
          });
          setFiles(e.file_list || []);
        })
        .catch((err) => !off && setError(err.message || "Couldn't load it."));
    } else {
      setForm({
        description: "",
        vendor: "",
        amount: "",
        spent_on: initial?.status === "planned" ? "" : today(),
        category: "Other",
        account_id: data.accounts.find((a) => !a.archived && a.owner === "company")?.id || "",
        status: "paid",
        notes: "",
        ...initial,
      });
    }
    return () => {
      off = true;
    };
    // Set up once per expense: a background refresh must not reset what you're typing.
  }, [expenseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const planned = form?.status === "planned";

  async function addFiles(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setBusy("upload");
    setError("");
    try {
      for (const f of picked) {
        const small = await shrinkImage(f);
        const up = await uploadExpenseFile(small);
        setFiles((xs) => [...xs, up]);
      }
    } catch (err) {
      setError(err.message || "Couldn't add that file.");
    } finally {
      setBusy("");
    }
  }

  async function removeFile(f) {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    try {
      await deleteExpenseFile(f.id);
      setFiles((xs) => xs.filter((x) => x.id !== f.id));
      changed.current = true;
    } catch (err) {
      setError(err.message || "Couldn't remove it.");
    }
  }

  async function save(markBought) {
    setBusy("save");
    setError("");
    try {
      const body = {
        ...form,
        status: markBought ? "paid" : form.status,
        spent_on: markBought && !form.spent_on ? today() : form.spent_on,
        account_id: form.account_id || null,
        file_ids: files.map((f) => f.id),
      };
      await saveExpense(expenseId, body);
      onClose(true);
    } catch (err) {
      setError(err.message || "Couldn't save.");
      setBusy("");
    }
  }

  async function remove() {
    if (!window.confirm(`Delete “${form.description}”?`)) return;
    try {
      await deleteExpense(expenseId);
      onClose(true);
    } catch (err) {
      setError(err.message || "Couldn't delete it.");
    }
  }

  const accounts = data.accounts.filter((a) => !a.archived || a.id === Number(form?.account_id));
  const title = expenseId ? (planned ? "Still to buy" : "Expense") : planned ? "Add to the to-buy list" : "Add an expense";

  const footer = form && (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center justify-between gap-2 sm:flex-1">
        {expenseId ? (
          <Button variant="ghost" size="sm" onClick={remove} className="text-red-700">
            Delete
          </Button>
        ) : (
          <span />
        )}
        {planned && expenseId && (
          <Button variant="secondary" size="sm" onClick={() => save(true)} disabled={Boolean(busy)}>
            Bought it
          </Button>
        )}
      </div>
      <Button onClick={() => save(false)} disabled={Boolean(busy)} className="w-full sm:w-auto py-3 sm:py-2">
        {busy === "save" ? "Saving…" : "Save"}
      </Button>
    </div>
  );

  return (
    <Modal isOpen title={title} onClose={() => onClose(changed.current)} footer={footer} maxWidthClass="max-w-lg">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!form ? (
        <p className="py-8 text-center text-sm text-birdie-charcoal/50">Loading…</p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={planned ? "Estimated cost" : "Amount"}>
              <input
                value={form.amount}
                onChange={set("amount")}
                inputMode="decimal"
                placeholder="0.00"
                className={`${INPUT} text-lg font-semibold`}
                autoFocus={!expenseId}
              />
            </Field>
            <Field label={planned ? "Needed by (optional)" : "Date"}>
              <input type="date" value={form.spent_on} onChange={set("spent_on")} className={INPUT} />
            </Field>
          </div>
          <Field label="What was it?">
            <input value={form.description} onChange={set("description")} placeholder="Golf balls and towels" className={INPUT} />
          </Field>
          <Field label="Where from (optional)">
            <input value={form.vendor} onChange={set("vendor")} placeholder="Amazon" className={INPUT} />
          </Field>
          <ChipField label="Category">
            {data.categories.map((c) => (
              <Pick key={c} on={form.category === c} onClick={() => pick("category", c)}>
                {categoryIcon(c)} {c}
              </Pick>
            ))}
          </ChipField>
          {!planned && (
            <ChipField label="Paid with" hint={ownerHint(data, form.account_id)}>
              {accounts.map((a) => (
                <Pick key={a.id} on={Number(form.account_id) === a.id} onClick={() => pick("account_id", a.id)}>
                  {a.kind === "card" ? "💳" : "🏦"} {a.name}
                  {a.owner !== "company" ? ` (${a.owner}'s)` : ""}
                </Pick>
              ))}
              <Pick on={!form.account_id} onClick={() => pick("account_id", "")} muted>
                Not sure yet
              </Pick>
            </ChipField>
          )}
          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={set("notes")} rows={2} className={INPUT} />
          </Field>

          <div>
            <div className="text-xs font-semibold text-birdie-charcoal/70 mb-1">Receipts</div>
            {files.length > 0 && (
              <ul className="space-y-1 mb-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-2 rounded-lg bg-birdie-cream/70 px-3 py-2 text-sm">
                    <button type="button" onClick={() => setViewing(f)} className="min-w-0 truncate text-left text-birdie-green font-medium">
                      {f.content_type === "application/pdf" ? "📄" : "🧾"} {f.filename}
                    </button>
                    <button type="button" onClick={() => removeFile(f)} className="shrink-0 text-xs text-birdie-charcoal/50">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy === "upload"}>
              {busy === "upload" ? "Adding…" : "📎 Attach photo or PDF"}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={addFiles}
            />
          </div>
        </div>
      )}

      {viewing && <FileViewer file={viewing} onClose={() => setViewing(null)} />}
    </Modal>
  );
}

function ownerHint(data, accountId) {
  const a = data.accounts.find((x) => x.id === Number(accountId));
  if (!a) return "Pick the card or account, so it's clear whose money paid.";
  return a.owner === "company" ? "Company money." : `Counts as money ${a.owner} put in.`;
}

export const INPUT =
  "w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm text-birdie-charcoal focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40";

// A row of one-tap choices. A div, not a label: tapping the heading mustn't pick the first chip.
function ChipField({ label, hint, children }) {
  return (
    <div role="group" aria-label={label}>
      <div className="text-xs font-semibold text-birdie-charcoal/70 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
      {hint && <div className="text-[11px] text-birdie-charcoal/45 mt-1">{hint}</div>}
    </div>
  );
}

function Pick({ on, onClick, muted, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`min-h-0 rounded-full border px-2.5 py-1.5 text-xs font-semibold transition ${
        on
          ? "bg-birdie-green text-white border-birdie-green"
          : muted
            ? "bg-white text-birdie-charcoal/50 border-birdie-charcoal/15"
            : "bg-white text-birdie-charcoal/75 border-birdie-green/20 hover:bg-birdie-cream"
      }`}
    >
      {children}
    </button>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-birdie-charcoal/45 mt-1">{hint}</span>}
    </label>
  );
}
